import { Link } from 'react-router-dom';

export function PrivacyPage() {
  return (
    <>
      <style>{`
        .legal-page h2 { margin-top: 2rem; margin-bottom: 0.5rem; font-size: 1.25rem; }
        .legal-page h3 { margin-top: 1.5rem; margin-bottom: 0.5rem; font-size: 1.1rem; }
        .legal-page p, .legal-page ul { margin-bottom: 1rem; }
        .legal-page ul { padding-left: 1.5rem; }
        .legal-page li { margin-bottom: 0.5rem; }
        .legal-page .legal-date { color: var(--text-muted); font-size: 0.9rem; margin-bottom: 2rem; }
      `}</style>
      <div className="container legal-page">
        <div style={{ marginBottom: '2rem' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', color: 'var(--text-color)' }}>
            <img src="/img/logo.svg" alt="SimplyTelehealth" style={{ width: '36px', height: '36px' }} />
            <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>SimplyTelehealth</span>
          </Link>
        </div>

        <h1>Privacy Policy</h1>
        <p className="legal-date">Effective Date: March 26, 2026</p>

        <p>SimplyTelehealth ("we," "us," or "our") provides a telehealth platform that enables healthcare providers ("Providers") to conduct video and text-based visits with their patients ("Patients"). This Privacy Policy describes how we collect, use, and protect your information.</p>

        <h2>1. Information We Collect</h2>

        <h3>Provider Information</h3>
        <ul>
          <li>Name, email address, and phone number</li>
          <li>Practice name, branding assets (logo, colors), and scheduling preferences</li>
          <li>Payment information (processed by Stripe; we do not store card numbers)</li>
        </ul>

        <h3>Patient Information</h3>
        <ul>
          <li>Name, email address, and phone number (when provided by the patient or their provider)</li>
          <li>SMS consent status and timestamps</li>
          <li>Visit metadata (scheduled times, duration, visit type)</li>
        </ul>

        <h3>Information We Do NOT Collect or Store</h3>
        <ul>
          <li>Video or audio recordings of telehealth visits</li>
          <li>The content of SMS text messages exchanged during SMS visits (message bodies are relayed in real-time and not stored in our database)</li>
          <li>Medical records, diagnoses, prescriptions, or other Protected Health Information (PHI) beyond visit metadata</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>To facilitate video and SMS-based telehealth visits between Providers and Patients</li>
          <li>To send visit reminders via email and SMS (with consent)</li>
          <li>To relay SMS messages between Providers and Patients during text-based visits without exposing personal phone numbers</li>
          <li>To manage Provider accounts, billing, and practice settings</li>
          <li>To maintain audit trails for compliance purposes (consent timestamps, message counts)</li>
        </ul>

        <h2>3. SMS and Text Messaging</h2>
        <p>We use SMS for two purposes:</p>
        <ul>
          <li><strong>Visit reminders:</strong> Appointment reminders sent before scheduled visits. Patients must opt-in before receiving these messages.</li>
          <li><strong>SMS visits:</strong> Two-way text conversations between a Provider and Patient, relayed through a SimplyTelehealth proxy phone number. The Provider initiates the session, and the Patient's first reply constitutes consent.</li>
        </ul>
        <p>We do not store the content of SMS messages. Only metadata (message count, timestamps, consent records) is retained. Message content is relayed through Twilio's HIPAA-eligible infrastructure.</p>
        <p>Message and data rates may apply. Message frequency varies: visit reminders are sent up to 2 times per appointment; SMS visit message frequency depends on the clinical conversation.</p>
        <p>You can opt out of SMS messages at any time by replying <strong>STOP</strong> to any message. Reply <strong>HELP</strong> for assistance.</p>

        <h2>4. Third-Party Services</h2>
        <p>We use the following third-party services to operate our platform:</p>
        <ul>
          <li><strong>Twilio</strong> — Video calls, phone dial-in, and SMS messaging. We maintain a HIPAA Business Associate Agreement (BAA) with Twilio.</li>
          <li><strong>Firebase (Google Cloud)</strong> — Authentication, database, and hosting.</li>
          <li><strong>Stripe</strong> — Payment processing. Stripe's privacy policy governs payment data.</li>
          <li><strong>SendGrid</strong> — Email delivery for invitations and reminders.</li>
        </ul>

        <h2>5. HIPAA Compliance</h2>
        <p>SimplyTelehealth is designed to support HIPAA compliance for healthcare providers:</p>
        <ul>
          <li>We maintain Business Associate Agreements (BAAs) with our infrastructure providers</li>
          <li>SMS message content is not stored in our systems</li>
          <li>SMS visit sessions automatically expire after 24 hours</li>
          <li>Patient consent is collected and recorded before any SMS communication</li>
          <li>All data is transmitted over encrypted connections (TLS/HTTPS)</li>
        </ul>
        <p>Providers are responsible for their own HIPAA compliance and should avoid sending sensitive PHI (diagnoses, test results, SSNs) via SMS text messages.</p>

        <h2>6. Data Security</h2>
        <ul>
          <li>All data is transmitted over HTTPS/TLS encryption</li>
          <li>Authentication uses Firebase Auth with secure token verification</li>
          <li>Database access is restricted by server-side security rules</li>
          <li>Patient visit links use cryptographically random tokens</li>
          <li>Provider phone numbers are never exposed to patients (and vice versa) during SMS visits</li>
        </ul>

        <h2>7. Data Retention</h2>
        <ul>
          <li>Provider accounts and practice data are retained while the account is active</li>
          <li>Visit metadata is retained for compliance and audit purposes</li>
          <li>SMS visit session routing data is deleted when the session ends or expires</li>
          <li>SMS message content is never stored by SimplyTelehealth</li>
        </ul>

        <h2>8. Your Rights</h2>
        <p>You may:</p>
        <ul>
          <li>Access, correct, or delete your personal information by contacting us</li>
          <li>Opt out of SMS messages by replying STOP</li>
          <li>Close your provider account by contacting support</li>
        </ul>

        <h2>9. Children's Privacy</h2>
        <p>SimplyTelehealth is not directed at children under 13. We do not knowingly collect personal information from children under 13.</p>

        <h2>10. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify registered Providers of material changes via email. The "Effective Date" at the top indicates when this policy was last revised.</p>

        <h2>11. Contact Us</h2>
        <p>If you have questions about this Privacy Policy, contact us at:</p>
        <p>
          SimplyTelehealth<br />
          Email: <a href="mailto:support@simplytelehealth.md">support@simplytelehealth.md</a><br />
          Website: <a href="https://simplytelehealth.md">simplytelehealth.md</a>
        </p>
      </div>
    </>
  );
}
